import { useState, useRef, useEffect } from "react"
import type { DiscoverSavedSearch } from "~/react/types/discover"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuDivider,
  DropdownMenuTitle,
  DropdownSearchInput,
} from "~/react/ui/dropdown-menu"
import { Tag, TagLabel } from "@/react/ui/tag"
import { discover_saved_searches_path } from "~/lib/routes"

const t = (key: string, options?: Record<string, unknown>) => window.i18n.t(key, options)

interface SavedSearchesListProps {
  savedSearches: DiscoverSavedSearch[]
  onLoadSearch: (search: DiscoverSavedSearch) => void
  searchQuery: string
}

function SavedSearchesList({ savedSearches, onLoadSearch, searchQuery }: SavedSearchesListProps) {
  const filteredSearches = savedSearches.filter((search) =>
    search.name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  if (savedSearches.length === 0) {
    return (
      <div className="empty-space empty-space--sm p-4">
        <strong className="empty-space__title">{t("js.back.discover.no_saved_searches.title")}</strong>
        <p className="empty-space__desc">{t("js.back.discover.no_saved_searches.description")}</p>
      </div>
    )
  }

  if (filteredSearches.length === 0) {
    return (
      <div className="empty-space empty-space--sm p-4">
        <strong className="empty-space__title">{t("js.back.discover.saved_searches.no_results_title")}</strong>
        <p className="empty-space__desc">{t("js.back.discover.saved_searches.no_results_description")}</p>
      </div>
    )
  }

  return (
    <>
      {filteredSearches.map((search) => (
        <DropdownMenuItem key={search.id} onClick={() => onLoadSearch(search)} closeOnClick small>
          <div className="saved-searches-list__item">
            <span className="saved-searches-list__title">{search.name}</span>
            {search.resultsCount !== undefined && (
              <Tag size="sm" className="saved-searches-list__count">
                <TagLabel>
                  {t("js.back.discover.filters.result", {
                    count: search.resultsCount,
                  })}
                </TagLabel>
              </Tag>
            )}
          </div>
        </DropdownMenuItem>
      ))}
    </>
  )
}

interface SavedSearchesButtonProps {
  savedSearches: DiscoverSavedSearch[]
  hasFilters: boolean
  newResults?: boolean
  onLoadSearch: (search: DiscoverSavedSearch) => void
}

export function SavedSearchesButton({ savedSearches, hasFilters, newResults, onLoadSearch }: SavedSearchesButtonProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleManageSavedSearches = () => {
    ;(window as any).Turbo.visit(discover_saved_searches_path(), {
      frame: "_top",
    })
  }

  // Focus search input when dropdown opens
  useEffect(() => {
    if (!dropdownRef.current) return

    const dropdownElement = dropdownRef.current
    const triggerElement = dropdownElement.querySelector('[data-bs-toggle="dropdown"]')

    if (!triggerElement) return

    const handleShow = () => {
      // Small delay to ensure the dropdown is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }

    triggerElement.addEventListener("shown.bs.dropdown", handleShow)

    return () => {
      triggerElement.removeEventListener("shown.bs.dropdown", handleShow)
    }
  }, [])

  return (
    <DropdownMenu
      ref={dropdownRef}
      className={`saved-searches-button ${!hasFilters ? "saved-searches-button--full" : ""}`.trim()}
    >
      <DropdownMenuTrigger asChild>
        {hasFilters ? (
          <Button size="sm" iconOnly aria-label={t("js.back.discover.load_search")}>
            <Icon icon="folder-bookmark" aria-hidden="true" />
          </Button>
        ) : (
          <Button size="sm" fullWidth>
            <Icon icon="folder-bookmark" aria-hidden="true" />
            <span className="saved-searches-button__text">
              {t("js.back.discover.saved_searches.title")}
              {newResults && (
                <Tag variant="info" size="sm">
                  <TagLabel>{t("js.back.discover.saved_searches.new_results")}</TagLabel>
                </Tag>
              )}
            </span>
            <Icon icon="chevron-down" aria-hidden="true" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="saved-searches-button__dropdown">
        {savedSearches.length > 0 && (
          <DropdownMenuItem className="dropdown-search dropdown-search--alternative">
            <DropdownSearchInput
              ref={searchInputRef}
              className="dropdown-search__input--alternative"
              placeholder={t("js.back.discover.saved_searches.search_placeholder")}
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            />
          </DropdownMenuItem>
        )}
        <DropdownMenuTitle small className="mt-3">
          {t("js.back.discover.saved_searches.title")}
        </DropdownMenuTitle>
        <SavedSearchesList savedSearches={savedSearches} onLoadSearch={onLoadSearch} searchQuery={searchQuery} />
        <DropdownMenuDivider />
        <DropdownMenuItem
          className="saved-searches-list__footer"
          closeOnClick
          onClick={handleManageSavedSearches}
          small
        >
          <div className="saved-searches-list__item">
            <Icon icon="list-ul" aria-hidden="true" className="saved-searches-list__footer-icon" />
            <span className="saved-searches-list__title">
              {t("js.back.discover.saved_searches.saved_searches_page_link")}
            </span>
            <Icon icon="arrow-right" aria-hidden="true" className="saved-searches-list__footer-arrow" />
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
